import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Type, Upload } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { faceFamily, getCustomFonts, onFontsChange, refreshCustomFonts } from "../lib/fonts";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function FontLibrary() {
  const [fonts, setFonts] = useState(getCustomFonts());
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { font, templates }
  const fileRef = useRef(null);

  useEffect(() => {
    refreshCustomFonts();
  }, []);
  useEffect(() => onFontsChange(setFonts), []);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/fonts", fd);
        ok += 1;
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(`${f.name}: ${typeof d === "string" ? d : "upload failed"}`);
      }
    }
    await refreshCustomFonts();
    if (ok) toast.success(`${ok} font${ok > 1 ? "s" : ""} added to the library`);
    setUploading(false);
  };

  const remove = async (font, force = false) => {
    try {
      await api.delete(`/fonts/${font.id}${force ? "?force=true" : ""}`);
      toast.success(
        force
          ? "Font deleted — affected elements now fall back to Classic Serif"
          : "Font deleted"
      );
      setConfirm(null);
      refreshCustomFonts();
    } catch (e) {
      if (e.response?.status === 409) {
        const d = e.response.data?.detail || {};
        setConfirm({ font, templates: d.templates || [] });
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  };

  return (
    <div data-testid="font-library">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-xs text-slate-500">
          Upload .ttf, .otf, .woff or .woff2 files. Custom fonts can be applied to any text
          element in the Template Designer and are embedded into generated PDFs.
        </p>
        <Button
          data-testid="font-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="shrink-0 gap-1.5"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Upload fonts
        </Button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".ttf,.otf,.woff,.woff2"
          className="hidden"
          data-testid="font-upload-input"
          onChange={(e) => {
            upload(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {fonts.length === 0 ? (
        <div
          className="border border-dashed border-slate-300 rounded-xl py-10 text-center text-sm text-slate-400"
          data-testid="font-library-empty"
        >
          <Type className="mx-auto mb-2 text-slate-300" size={28} />
          No custom fonts yet — upload your first font file.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {fonts.map((f) => (
            <div
              key={f.id}
              data-testid={`font-card-${f.id}`}
              className="border border-slate-200 rounded-xl p-3.5 bg-white"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{f.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {f.style} · {(f.original_format || "").toUpperCase()} ·{" "}
                    {new Date(f.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  data-testid={`font-delete-${f.id}`}
                  onClick={() => remove(f)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-soft"
                  title="Delete font"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div
                className="mt-2.5 text-xl text-slate-800 leading-snug truncate"
                style={{ fontFamily: `'${faceFamily(f)}', Georgia, serif` }}
                data-testid={`font-preview-${f.id}`}
              >
                AaBbGgDd 0123 — Lovely Institute
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="font-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Font is in use</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block">
                "{confirm?.font?.name}" is used by {confirm?.templates?.length || 0}{" "}
                template(s):
              </span>
              <span className="block mt-1.5 font-medium text-slate-700">
                {confirm?.templates?.join(", ")}
              </span>
              <span className="block mt-1.5">
                Deleting it will make those elements fall back to Classic Serif in the
                designer and in generated PDFs.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="font-delete-cancel">Keep font</AlertDialogCancel>
            <AlertDialogAction
              data-testid="font-delete-force"
              onClick={() => remove(confirm.font, true)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
